import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Upload, FileSpreadsheet, Search } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ImportIndicateurs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [typesPersonnel, setTypesPersonnel] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTypesPersonnel = async () => {
    try {
      setLoading(true);
      
      const [typesRes, personnelRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/personnel').catch(() => ({ data: [] }))
      ]);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];

      // Si contexte filiale spécifique, filtrer les types qui ont du personnel dans cette filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        const personnelInFiliale = allPersonnel.filter(p => p.filiale_id === parseInt(filialeId));
        const typeIdsInFiliale = [...new Set(personnelInFiliale.map(p => p.type_personnel_id))];
        const filteredTypes = allTypes.filter(t => typeIdsInFiliale.includes(t.idtype_personnel));
        setTypesPersonnel(filteredTypes);
        setFilteredTypes(filteredTypes);
      } else {
        setTypesPersonnel(allTypes);
        setFilteredTypes(allTypes);
      }
    } catch (error) {
      console.error('Erreur récupération types personnel:', error);
      setTypesPersonnel([]);
      setFilteredTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypesPersonnel();
  }, [filialeId, isConsolidated]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = typesPersonnel.filter(type =>
        type.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.idtype_personnel?.toString().includes(searchTerm)
      );
      setFilteredTypes(filtered);
    } else {
      setFilteredTypes(typesPersonnel);
    }
  }, [searchTerm, typesPersonnel]);

  const handleImport = (typeId) => {
    navigate(`/import-performance/${typeId}`);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Importation Indicateurs"
        subtitle="Sélectionnez un type de personnel pour importer les indicateurs de performance"
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchTypesPersonnel,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un type de personnel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Types de Personnel</h3>
            <p className="text-sm text-gray-500 mt-1">Cliquez sur un type pour accéder à l'importation des indicateurs</p>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredTypes.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                {searchTerm ? 'Aucun type de personnel trouvé' : 'Aucun type de personnel trouvé'}
              </div>
            ) : (
              filteredTypes.map((type) => (
                <div
                  key={type.idtype_personnel}
                  className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleImport(type.idtype_personnel)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <FileSpreadsheet className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{type.libelle}</div>
                      <div className="text-xs text-gray-500 mt-1">ID: {type.idtype_personnel}</div>
                    </div>
                  </div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Upload className="h-4 w-4" />
                    Importer
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportIndicateurs;
